import {
  DACR_LOCAL_ITEMS_KEY,
  DACR_PENDING_REMOVAL_KEY,
} from '@/features/explorer/store'
import type { Item } from '@/shared/graphql/router/graphql'

const LOCAL_ITEM_TTL_MS = 10 * 60 * 1000
const QUERY_MATCH_GRACE_MS = 5 * 1000

type LocalExplorerItem = {
  folderId: string
  createdAt: number
  item: Item
}

const isLocalExplorerItem = (value: unknown): value is LocalExplorerItem => {
  if (!value || typeof value !== 'object') return false

  const entry = value as Partial<LocalExplorerItem>
  return (
    typeof entry.folderId === 'string' &&
    typeof entry.createdAt === 'number' &&
    !!entry.item &&
    typeof entry.item.id === 'string'
  )
}

const readLocalExplorerItems = (): LocalExplorerItem[] => {
  if (typeof localStorage === 'undefined') return []

  try {
    const raw = localStorage.getItem(DACR_LOCAL_ITEMS_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    return parsed.filter(isLocalExplorerItem)
  } catch {
    return []
  }
}

const writeLocalExplorerItems = (items: LocalExplorerItem[]) => {
  if (typeof localStorage === 'undefined') return

  try {
    localStorage.setItem(DACR_LOCAL_ITEMS_KEY, JSON.stringify(items))
  } catch {
    // Ignore storage failures; Apollo cache still drives the optimistic row.
  }
}

const isFresh = (entry: LocalExplorerItem, now: number) =>
  now - entry.createdAt < LOCAL_ITEM_TTL_MS

export const upsertLocalExplorerItem = (folderId: string, item: Item) => {
  const items = readLocalExplorerItems().filter(
    (entry) => entry.item.id !== item.id
  )

  writeLocalExplorerItems([
    ...items,
    {
      folderId,
      createdAt: Date.now(),
      item,
    },
  ])
}

export const pruneLocalExplorerItems = (
  folderId: string,
  queryItems: Item[],
  now = Date.now()
) => {
  const queryItemIds = new Set(queryItems.map((item) => item.id))

  const items = readLocalExplorerItems().filter((entry) => {
    if (!isFresh(entry, now)) return false
    if (entry.folderId !== folderId) return true
    if (!queryItemIds.has(entry.item.id)) return true

    return now - entry.createdAt < QUERY_MATCH_GRACE_MS
  })

  writeLocalExplorerItems(items)
}

export const mergeLocalExplorerItems = (
  folderId: string,
  queryItems: Item[],
  now = Date.now()
) => {
  const queryItemIds = new Set(queryItems.map((item) => item.id))
  const localItems = readLocalExplorerItems()
    .filter((entry) => entry.folderId === folderId)
    .filter((entry) => isFresh(entry, now))
    .filter((entry) => !queryItemIds.has(entry.item.id))
    .map((entry) => entry.item)

  return [...localItems, ...queryItems]
}

type PendingRemovalItem = {
  folderId: string
  itemId: string
  createdAt: number
}

const isPendingRemovalItem = (value: unknown): value is PendingRemovalItem => {
  if (!value || typeof value !== 'object') return false

  const entry = value as Partial<PendingRemovalItem>
  return (
    typeof entry.folderId === 'string' &&
    typeof entry.itemId === 'string' &&
    typeof entry.createdAt === 'number'
  )
}

const readPendingRemovalItems = (): PendingRemovalItem[] => {
  if (typeof localStorage === 'undefined') return []

  try {
    const raw = localStorage.getItem(DACR_PENDING_REMOVAL_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    return parsed.filter(isPendingRemovalItem)
  } catch {
    return []
  }
}

const writePendingRemovalItems = (items: PendingRemovalItem[]) => {
  if (typeof localStorage === 'undefined') return

  try {
    localStorage.setItem(DACR_PENDING_REMOVAL_KEY, JSON.stringify(items))
  } catch {
    // Ignore storage failures; Apollo cache still drives the optimistic removal.
  }
}

const isPendingFresh = (entry: PendingRemovalItem, now: number) =>
  now - entry.createdAt < LOCAL_ITEM_TTL_MS

export const addPendingRemovalItems = (folderId: string, itemIds: string[]) => {
  const now = Date.now()
  const removalIds = new Set(itemIds)

  const items = readPendingRemovalItems().filter(
    (entry) => !(entry.folderId === folderId && removalIds.has(entry.itemId))
  )

  writePendingRemovalItems([
    ...items,
    ...itemIds.map((itemId) => ({ folderId, itemId, createdAt: now })),
  ])
}

export const prunePendingRemovalItems = (
  folderId: string,
  queryItems: Item[],
  now = Date.now()
) => {
  const queryItemIds = new Set(queryItems.map((item) => item.id))

  const items = readPendingRemovalItems().filter((entry) => {
    if (!isPendingFresh(entry, now)) return false
    if (entry.folderId !== folderId) return true
    if (queryItemIds.has(entry.itemId)) return true

    return now - entry.createdAt < QUERY_MATCH_GRACE_MS
  })

  writePendingRemovalItems(items)
}

export const filterPendingRemovalItems = (
  folderId: string,
  queryItems: Item[],
  now = Date.now()
) => {
  const pendingIds = new Set(
    readPendingRemovalItems()
      .filter((entry) => entry.folderId === folderId)
      .filter((entry) => isPendingFresh(entry, now))
      .map((entry) => entry.itemId)
  )

  return queryItems.filter((item) => !pendingIds.has(item.id))
}
